'use client';

import Link from 'next/link';
import classes from './NeumoHero.module.scss';
import { Loader } from '@mantine/core';
import { useEffect, useState, useRef } from 'react';

type FeaturePoint = {
  label: string;
  description: string;
};

type Stat = {
  value: string;
  label: string;
  helper?: string;
};

type NeumoHeroProps = {
  badge?: string;
  catchphrase?: string;
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  phone?: string;
  whatsapp?: string;
  highlights?: FeaturePoint[];
  stats?: Stat[];
  cardTitle?: string;
  cardBadge?: string;
  fakeDoorBadge?: string;
  fakeDoorText?: string;
  fakeDoorButton?: string;
};

export const NeumoHero = ({ data }: { data: NeumoHeroProps }) => {
  const {
    badge,
    catchphrase,
    title,
    description,
    primaryLabel,
    primaryHref,
    secondaryLabel,
    secondaryHref,
    phone,
    whatsapp,
    highlights = [],
    stats = [],
    cardTitle = "Notre système génère automatiquement",
    cardBadge = "En moins de 3 minutes",
    fakeDoorBadge = "Version gratuite",
    fakeDoorText = "100 e-mails ciblés et personnalisés par jour",
    fakeDoorButton = "Demander un accès anticipé",
  } = data;

  // State to track which items are checked (finished loading)
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const [loadingItems, setLoadingItems] = useState<Set<number>>(new Set());
  const [showLastSentence, setShowLastSentence] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const hasItemsAnimatedRef = useRef(false);

  // Show "Automatiquement." after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLastSentence(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Initialize all items in loading state
  useEffect(() => {
    if (highlights.length === 0) return;
    // Start all items in loading state immediately
    setLoadingItems(new Set(highlights.map((_, i) => i)));
    setCheckedItems(new Set());
  }, [highlights]);

  // Animate items: check one by one every 0.6s when card enters viewport
  useEffect(() => {
    if (highlights.length === 0 || hasItemsAnimatedRef.current) return;

    // Check if card is in viewport using Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasItemsAnimatedRef.current) {
            // Check items one by one every 0.6s
            const timeouts: NodeJS.Timeout[] = [];
            highlights.forEach((_, index) => {
              // Finish loading this item (check it)
              const checkTimeout = setTimeout(() => {
                setLoadingItems((prev) => {
                  const next = new Set(prev);
                  next.delete(index);
                  return next;
                });
                setCheckedItems((prev) => {
                  const next = new Set(prev);
                  next.add(index);
                  return next;
                });
              }, (index + 1) * 600); // Check at 0.6s, 1.2s, 1.8s, etc.
              timeouts.push(checkTimeout);
            });

            hasItemsAnimatedRef.current = true;
          }
        });
      },
      { threshold: 0.2 } // Trigger when 20% of the element is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [highlights]);

  return (
    <section className={classes.hero}>
      <div className={classes.container}>
        <div className={classes.left}>
          {catchphrase && (
            <p className={classes.catchphrase}>{catchphrase}</p>
          )}
          <h1 className={classes.title}>
            <div className={classes.titleFinal}>
              {title.split('.').filter(s => s.trim()).map((sentence, index, array) => {
                const isSecondSentence = index === 1 && array.length >= 3;
                const isLastSentence = index === array.length - 1;
                
                return (
                  <div 
                    key={index} 
                    className={`${classes.titleLine} ${isSecondSentence ? classes.titleSentenceSmall : ''}`}
                  >
                    {sentence.trim()}.
                  </div>
                );
              })}
            </div>
          </h1>
          {description && <p className={classes.description}>{description}</p>}

          {stats.length > 0 && (
            <div className={classes.statsGrid}>
              {stats.map((stat, index) => (
                <div key={index} className={classes.statCard}>
                  <span className={classes.statValue}>{stat.value}</span>
                  <span className={classes.statLabel}>{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={classes.right}>
          <div className={classes.aiHeadVisual} aria-hidden="true">
            <div className={classes.aiHeadGlow}></div>
          </div>
          <div className={classes.card} ref={cardRef}>
            <div className={classes.cardHeader}>
              <h2 className={classes.cardTitle}>{cardTitle}</h2>
              <div className={classes.cardBadge}>{cardBadge}</div>
            </div>
            <ul className={classes.list}>
              {highlights.map((item, index) => {
                const isLoading = loadingItems.has(index);
                const isChecked = checkedItems.has(index);

                return (
                  <li key={index} className={classes.listItem}>
                    <div className={classes.listItemContent}>
                        <>
                          <div className={classes.listIcon}>
                            {!isLoading && isChecked ? (<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <defs>
                                <linearGradient id={`gradientIcon-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#1e3a8a" />
                                  <stop offset="50%" stopColor="#2563eb" />
                                  <stop offset="100%" stopColor="#38bdf8" />
                                </linearGradient>
                              </defs>
                              <circle cx="10" cy="10" r="8" fill={`url(#gradientIcon-${index})`} />
                              <path
                                d="M6 10L9 13L14 7"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>) : <Loader color="#1bbd31ff" size="xs" />}
                          </div>
                          <div className={classes.listContent}>
                            <strong className={classes.listLabel}>{item.label}</strong>
                          </div>
                        </>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className={classes.contactUsWrapper}>
              <Link href="/contact" className={`${classes.cta} ${classes.contactUsBtn}`}>
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

